import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Box,
    Chip,
    TextField,
    InputAdornment,
    tableCellClasses,
    Autocomplete,
    Select,
    MenuItem
} from '@mui/material';
import { FC, useEffect, useState, useCallback } from 'react';
import styled_ from "@emotion/styled";
import { Utils } from "../../utils/common-utils";
import SearchIcon from '@mui/icons-material/Search';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchPartyBalances, searchParties } from '../../store/reportStore/reportThunk';
import { clearPartyBalances, clearSearchedParties } from '../../store/reportStore/reportslice';
import { ApiStatus } from '../../utils/api.types';
import { Party, PartyBalance } from '../../utils/report.types';
import ReusableReportHeader from './ReusableReportHeader';
import Filters from '../Filters/Filters';

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#eaecf0',
        color: '#000',
        border: '0.75px solid #d9d4d4',
        margin: 0,
        padding: 8,
        fontSize: '10pt',
        fontWeight: 600,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: '9pt',
        fontWeight: 'normal',
        color: '#292d35',
        padding: 8,
        border: '0.75px solid #e2e8f0',
        margin: 0,
        backgroundColor: '#fff',
    },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: '#f8fafc',
    },
    '&:hover': {
        backgroundColor: '#f1f5f9',
    },
}));

const PartyBalances: FC = () => {
    const dispatch = useAppDispatch();

    // Redux state
    const {
        partyBalances,
        partyBalancesPagination,
        partyBalancesSummary,
        searchedParties,
        status
    } = useAppSelector((state) => state.reports);

    const { selectedAccount } = useAppSelector((state) => state.auth);
    const accountId = selectedAccount?._id || '';

    const loading = status === ApiStatus.LOADING;
    const [loadingMore, setLoadingMore] = useState(false);
    const [filter, setFilter] = useState<'all' | 'client' | 'vendor'>('all');

    // Autocomplete search states
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedParty, setSelectedParty] = useState<Party | null>(null);
    const [autocompleteLoading, setAutocompleteLoading] = useState(false);

    // Date range filter states. Filters overwrites these on mount with its own default.
    const [selectedDateRangeValue, setSelectedDateRangeValue] = useState('Last 30 Days');
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        endDate: new Date()
    });
    const [filterData, setFilterData] = useState<any>(null);

    const formatDateToISO = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const searchPartiesHandler = useCallback(async (query: string) => {
        if (!accountId || !query.trim()) return;

        setAutocompleteLoading(true);
        try {
            await dispatch(searchParties({
                accountId,
                query,
                limit: 20
            })).unwrap();
        } catch (error) {
            console.error('Error searching parties:', error);
        } finally {
            setAutocompleteLoading(false);
        }
    }, [dispatch, accountId]);

    const fetchPartiesBalances = useCallback(async (page: number = 1) => {
        const isFirstPage = page === 1;

        if (!isFirstPage) {
            setLoadingMore(true);
        }

        try {
            console.log(`Fetching parties balances page ${page}...`);

            // Build query parameters - use dateFrom/dateTo for both opening balance and transactions
            const dateFromISO = formatDateToISO(dateRange.startDate);
            const dateToISO = formatDateToISO(dateRange.endDate);

            const params: any = {
                accountId,
                dateFrom: dateFromISO,
                dateTo: dateToISO,
                page,
                limit: 20
            };

            // Add selected party filter
            if (selectedParty) {
                params.partyIds = selectedParty.partyId;
            }

            // Add party type filter
            if (filter !== 'all') {
                params.partyType = filter;
            }

            console.log('Fetching with params:', params);

            await dispatch(fetchPartyBalances(params)).unwrap();
            console.log(`Page ${page}: parties loaded successfully`);

            // Update filter data for the header
            setFilterData({
                periodString: `${formatDate(dateFromISO)} to ${formatDate(dateToISO)}`
            });
        } catch (error) {
            console.error('Error fetching parties balances:', error);
        } finally {
            setLoadingMore(false);
        }
    }, [dispatch, accountId, dateRange, selectedParty, filter]);

    // Fetch parties balances when filters change
    useEffect(() => {
        if (accountId) {
            // Clear existing data and fetch first page
            dispatch(clearPartyBalances());
            fetchPartiesBalances(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, dateRange, selectedParty, filter, dispatch]);

    // Debounced autocomplete search
    useEffect(() => {
        if (!searchQuery.trim()) {
            dispatch(clearSearchedParties());
            return;
        }

        const timeoutId = setTimeout(async () => {
            await searchPartiesHandler(searchQuery);
        }, 300); // 300ms debounce

        return () => clearTimeout(timeoutId);
    }, [searchQuery, accountId, dispatch, searchPartiesHandler]);

    // The shared Filters bar owns the presets and the custom pickers; it hands back
    // a query string, so all this screen needs is the start/end out of it.
    const handleDateFilter = (queryString: string) => {
        const params = new URLSearchParams(String(queryString).replace(/^&/, ''));
        const start = params.get('start');
        const end = params.get('end');
        if (start && end) {
            setDateRange({ startDate: new Date(start), endDate: new Date(end) });
        }
    };

    // Handle scroll event for infinite loading
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        const scrolledToBottom =
            target.scrollHeight - target.scrollTop <= target.clientHeight + 100; // 100px threshold

        if (scrolledToBottom && partyBalancesPagination?.hasMore && !loadingMore && !loading) {
            console.log('Loading more parties...');
            fetchPartiesBalances(partyBalancesPagination.currentPage + 1);
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 2 }} className={'c-card2'}>
                    {/* Scrollable content area */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            minHeight: 0,
                            '&::-webkit-scrollbar': { display: 'none' },
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none'
                        }}
                        onScroll={handleScroll}
                    >
                        {/* Report Header */}
                        <ReusableReportHeader
                            reportType="PARTY_BALANCES"
                            title="Party-wise Opening & Closing Balances"
                            subtitle={(filterData && filterData.periodString) ? filterData.periodString : ''}
                            recordCount={partyBalancesPagination?.totalParties || 0}
                            isLoading={loading}
                            showPrint={false}
                            showShare={false}
                        />

                        {/* Search and Filter Bar */}
                        <Box style={{
                            padding: '16px 24px',
                            backgroundColor: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                            display: 'flex',
                            gap: '16px',
                            alignItems: 'center',
                            flexWrap: 'wrap'
                        }}>
                            {/* Party Search Autocomplete */}
                            <Autocomplete
                                options={searchedParties}
                                value={selectedParty}
                                onChange={(event, newValue) => {
                                    setSelectedParty(newValue);
                                }}
                                inputValue={searchQuery}
                                onInputChange={(event, newInputValue) => {
                                    setSearchQuery(newInputValue);
                                }}
                                getOptionLabel={(option) => option.partyName}
                                loading={autocompleteLoading}
                                loadingText="Searching..."
                                noOptionsText={searchQuery ? "No parties found" : "Type to search"}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box>
                                            <Typography style={{ fontSize: '9pt', fontWeight: 500 }}>
                                                {option.partyName}
                                            </Typography>
                                            {option.legalName && option.legalName !== option.partyName && (
                                                <Typography style={{ fontSize: '8pt', color: '#64748b' }}>
                                                    {option.legalName}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Chip
                                            label={option.partyType === 'client' ? 'Buyer' : 'Supplier'}
                                            size="small"
                                            style={{
                                                marginLeft: 'auto',
                                                fontSize: '7pt',
                                                height: '18px',
                                                backgroundColor: option.partyType === 'client' ? '#3b82f6' : '#dc2626',
                                                color: '#fff'
                                            }}
                                        />
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search parties..."
                                        className='c-text-field-3'
                                        variant="standard"
                                        name="search"
                                        size='small'
                                        InputProps={{
                                            ...params.InputProps,
                                            disableUnderline: true,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                style={{ width: '300px' }}
                            />

                            {/* Date range comes from the shared Filters bar (party search above stays local,
                                it shows Buyer/Supplier chips this component needs). */}
                            <Filters
                                type="party-balances"
                                inline
                                hidePartySearch
                                hideReset
                                noPersist
                                filterHandler={handleDateFilter}
                                selectedFilters={(data: any) => setSelectedDateRangeValue(data?.dateRangeLabel || '')}
                            />

                            {/* Type Filters */}
                            <Box style={{ display: 'flex', gap: '8px' }}>
                                <Chip
                                    label="All"
                                    onClick={() => setFilter('all')}
                                    color={filter === 'all' ? 'primary' : 'default'}
                                    style={{ cursor: 'pointer' }}
                                />
                                <Chip
                                    label="Buyers"
                                    onClick={() => setFilter('client')}
                                    color={filter === 'client' ? 'primary' : 'default'}
                                    style={{ cursor: 'pointer' }}
                                />
                                <Chip
                                    label="Suppliers"
                                    onClick={() => setFilter('vendor')}
                                    color={filter === 'vendor' ? 'primary' : 'default'}
                                    style={{ cursor: 'pointer' }}
                                />
                            </Box>

                            {/* Count and Active Filter Chip */}
                            <Box style={{ display: 'flex', gap: '8px', marginLeft: 'auto', alignItems: 'center' }}>
                                {selectedParty && (
                                    <Chip
                                        label={`Party: ${selectedParty.partyName}`}
                                        onDelete={() => setSelectedParty(null)}
                                        size="small"
                                        style={{ backgroundColor: '#10b981', color: '#fff' }}
                                    />
                                )}
                                {/* {partyBalancesSummary && (
                                    <Chip
                                        label={`Loaded: ${partyBalances.length} / ${partyBalancesPagination?.totalParties || 0}`}
                                        size="small"
                                        style={{ backgroundColor: '#3b82f6', color: '#fff' }}
                                    />
                                )} */}
                            </Box>
                        </Box>

                        {/* Table */}
                        {loading ? (
                            <Box style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '400px'
                            }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell style={{ width: '5%', textAlign: 'center' }}>#</StyledTableCell>
                                        <StyledTableCell style={{ width: '25%' }}>Party Name</StyledTableCell>
                                        <StyledTableCell style={{ width: '10%', textAlign: 'center' }}>Party</StyledTableCell>
                                        <StyledTableCell style={{ width: '15%', textAlign: 'right' }}>Opening Balance</StyledTableCell>
                                        <StyledTableCell style={{ width: '12%', textAlign: 'right' }}>Total Debit</StyledTableCell>
                                        <StyledTableCell style={{ width: '12%', textAlign: 'right' }}>Total Credit</StyledTableCell>
                                        <StyledTableCell style={{ width: '15%', textAlign: 'right' }}>Closing Balance</StyledTableCell>
                                        <StyledTableCell style={{ width: '6%', textAlign: 'center' }}>Entries</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {partyBalances.length > 0 ? (
                                        partyBalances.map((party: PartyBalance, index: number) => (
                                            <StyledTableRow key={party.partyId}>
                                                <StyledTableCell style={{ textAlign: 'center' }}>{index + 1}</StyledTableCell>
                                                <StyledTableCell>
                                                    <Box>
                                                        <Typography style={{ fontSize: '9pt', fontWeight: 500 }}>
                                                            {party.partyName}
                                                        </Typography>
                                                        {party.openingBalance.description && (
                                                            <Typography
                                                                style={{
                                                                    fontSize: '8pt',
                                                                    color: '#64748b',
                                                                    fontStyle: 'italic',
                                                                }}
                                                            >
                                                                {party.openingBalance.description}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>
                                                    <Chip
                                                        label={party.partyType === 'client' ? 'Buyer' : 'Supplier'}
                                                        size="small"
                                                        style={{
                                                            fontSize: '8pt',
                                                            height: '20px',
                                                            backgroundColor: party.partyType === 'client' ? '#3b82f6' : '#dc2626',
                                                            color: '#fff'
                                                        }}
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell style={{
                                                    textAlign: 'right',
                                                    color: party.openingBalance.type === 'Dr' ? '#292d35' : '#292d35',
                                                    fontWeight: 500
                                                }}>
                                                    {Utils.formatNumber(party.openingBalance.amount.toFixed(2))} {party.openingBalance.type}
                                                </StyledTableCell>
                                                <StyledTableCell style={{
                                                    textAlign: 'right',
                                                    color: '#292d35'
                                                }}>
                                                    {Utils.formatNumber(party.totalDebit.toFixed(2))}
                                                </StyledTableCell>
                                                <StyledTableCell style={{
                                                    textAlign: 'right',
                                                    color: '#292d35'
                                                }}>
                                                    {Utils.formatNumber(party.totalCredit.toFixed(2))}
                                                </StyledTableCell>
                                                <StyledTableCell style={{
                                                    textAlign: 'right',
                                                    color: party.closingBalance.type === 'Dr' ? '#292d35' : '#292d35',
                                                    fontWeight: 600,
                                                    fontSize: '9.5pt'
                                                }}>
                                                    {Utils.formatNumber(party.closingBalance.amount.toFixed(2))} {party.closingBalance.type}
                                                </StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center', fontSize: '8pt' }}>
                                                    {party.entryCount}
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <StyledTableCell colSpan={8} style={{ textAlign: 'center', padding: '32px' }}>
                                                <Typography style={{ color: '#64748b' }}>
                                                    {selectedParty || selectedDateRangeValue !== 'Last 30 Days' || filter !== 'all'
                                                        ? 'No parties found matching your filters'
                                                        : 'No parties found'}
                                                </Typography>
                                            </StyledTableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}

                        {/* Loading More Indicator */}
                        {loadingMore && (
                            <Box style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '16px'
                            }}>
                                <CircularProgress size={24} />
                                <Typography style={{ marginLeft: '12px', color: '#64748b', fontSize: '9pt' }}>
                                    Loading more parties...
                                </Typography>
                            </Box>
                        )}

                        {/* End of List Indicator */}
                        {!loading && !loadingMore && !partyBalancesPagination?.hasMore && partyBalances.length > 0 && (
                            <Box style={{
                                textAlign: 'center',
                                padding: '16px',
                                color: '#64748b',
                                fontSize: '9pt'
                            }}>
                                <Typography>
                                    All {partyBalancesPagination?.totalParties || 0} parties loaded
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
};

export default PartyBalances;
